import { useFetch } from 'hooks/useFetch/useFetch'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import type { RefundData, RefundDataRequest } from 'refunds/types/refundData'
import { useSession } from 'session/src/hooks/useSession'
import useSWRImmutable from 'swr/immutable'
import { env } from 'utils/envManager'
import { url } from 'utils/url'
import { useGuestEmail } from 'utils/useGuestEmail/useGuestEmail'

import { normalizeRefundData, normalizeVisibilityProps } from './normalizations'

export const useRefundData = (
	preloadRefundData?: RefundData,
	orderId?: string | null,
	disabled = false
) => {
	const { fetcher } = useFetch()
	const { country, pageType } = useMasterData()
	const { logged, isReady } = useSession()
	const guestEmail = useGuestEmail()
	const { countryISO } = country
	const isFetchEnabled = isReady && countryISO && !disabled
	const isGuestUser = !logged && guestEmail

	const params = {
		countryISO,
		...(isGuestUser && orderId && { orderId }),
	}

	const { data, isLoading, isValidating, error, mutate } =
		useSWRImmutable<RefundDataRequest | null>(
			isFetchEnabled && !preloadRefundData
				? {
						url: url(
							`${env.NEXT_PUBLIC_SITE_BASE_URL}/ws-refunds/methodsInfo`,
							params
						),
						...(isGuestUser && {
							extraHeaders: { 'X-Guest-Email': guestEmail },
						}),
					}
				: null,
			fetcher,
			{}
		)

	const refundData = data && normalizeRefundData(data, country)

	return {
		error,
		isLoading: isLoading || isValidating,
		refundData: preloadRefundData ?? refundData,
		isError: !!error,
		reload: mutate,
		visibilityProps: normalizeVisibilityProps(
			pageType,
			preloadRefundData ?? refundData
		),
	}
}
